import { Role } from '@interfaces/models';

export type PermissionType = 'WRITE' | 'READ';

export const PermissionFeatures = [
  'DASHBOARD',
  'DASHBOARD_ACCOUNT_SEE_ACCOUNT_DETAILS',
  'DASHBOARD_ACCOUNT_TOP_UP',
  'DASHBOARD_SERVICES_SEE_DETAILS',
  'DASHBOARD_NOTIFICATIONS_SEE_ALL',
  'DASHBOARD_TRANSACTIONS_HISTORY_SEE_ALL',
  'DASHBOARD_EMPLOYEES_SEE_ALL',
  'DASHBOARD_EMPLOYEES_ADD_NEW',
  'DASHBOARD_VEHICLES_SEE_ALL',
  'DASHBOARD_VEHICLES_ADD_NEW',

  'EMPLOYEES',
  'EMPLOYEES_ADD_EMPLOYEE',
  'EMPLOYEES_IMPORT_EMPLOYEES',
  'EMPLOYEES_EDIT_EMPLOYEE',
  'EMPLOYEES_REMOVE_EMPLOYEE',

  'EMPLOYEES_PROFILE',
  'EMPLOYEES_PROFILE_CREATE_PROFILE',
  'EMPLOYEES_PROFILE_EDIT_PROFILE',
  'EMPLOYEES_PROFILE_ASSIGN_EMPLOYEES_TO_PROFILE',
  'EMPLOYEES_PROFILE_MANAGE_SERVICES',

  'VEHICLES',
  'VEHICLES_ADD_VEHICLE',
  'VEHICLES_IMPORT_VEHICLES',
  'VEHICLES_EDIT_VEHICLE',
  'VEHICLES_REMOVE_VEHICLE',

  'PARKINGS',
  'PARKINGS_STOP_PARKING',

  'COST_CENTER',
  'COST_CENTER_CREATE_COST_CENTER',
  'COST_CENTER_DASHBOARD',
  'COST_CENTER_ACCOUNT_SEE_DETAILS',
  'COST_CENTER_ACCOUNT_EDIT_CARDS',
  'COST_CENTER_ACCOUNT_TOP_UP',
  'COST_CENTER_EDIT_COST_CENTER_ADDRESS',
  'COST_CENTER_EDIT_COST_CENTER_DEFAULT',
  'COST_CENTER_ASSIGN_USERS',
  'COST_CENTER_ASSIGN_INVOICING_RECIPIENTS',

  'SUBSCRIPTIONS',
  'SUBSCRIPTIONS_MODIFY',

  'REPORTS_FILES',
  'REPORTS_FILES_REPORTS',
  'REPORTS_FILES_TRANSACTION_HISTORY',
  'REPORTS_FILES_INVOICING',
  'REPORTS_FILES_GENERATE_REPORTS',
  'REPORTS_FILES_DOWNLOAD_INVOICE_AND_ATTACHMENTS',

  'HELP',
  'HELP_SEND_A_TICKET',

  'NOTIFICATIONS',
  'NOTIFICATIONS_MANAGE_NOTIFICATIONS',

  'USER_ACCOUNT_EDIT',

  'COMPANY_ACCOUNT_EDIT',
  'COMPANY_ACCOUNT_DELEGATE_PERMISSIONS',
  'COMPANY_ACCOUNT_LIST_OF_USERS',
  'COMPANY_ACCOUNT_INVITE_USER',
  'COMPANY_ACCOUNT_EDIT_USER',
] as const;

export type PermissionFeature = (typeof PermissionFeatures)[number];

export const PermissionMatrix: Record<PermissionFeature, Record<PermissionType, Role[]>> = {
  DASHBOARD: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_ACCOUNT_SEE_ACCOUNT_DETAILS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_ACCOUNT_TOP_UP: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },
  DASHBOARD_SERVICES_SEE_DETAILS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_NOTIFICATIONS_SEE_ALL: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_TRANSACTIONS_HISTORY_SEE_ALL: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_EMPLOYEES_SEE_ALL: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_EMPLOYEES_ADD_NEW: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  DASHBOARD_VEHICLES_SEE_ALL: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  DASHBOARD_VEHICLES_ADD_NEW: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  EMPLOYEES: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  EMPLOYEES_ADD_EMPLOYEE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  EMPLOYEES_IMPORT_EMPLOYEES: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  EMPLOYEES_EDIT_EMPLOYEE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  EMPLOYEES_REMOVE_EMPLOYEE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  EMPLOYEES_PROFILE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  EMPLOYEES_PROFILE_CREATE_PROFILE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },
  EMPLOYEES_PROFILE_EDIT_PROFILE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },
  EMPLOYEES_PROFILE_ASSIGN_EMPLOYEES_TO_PROFILE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  EMPLOYEES_PROFILE_MANAGE_SERVICES: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },

  VEHICLES: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  VEHICLES_ADD_VEHICLE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  VEHICLES_IMPORT_VEHICLES: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  VEHICLES_EDIT_VEHICLE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  VEHICLES_REMOVE_VEHICLE: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  PARKINGS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  PARKINGS_STOP_PARKING: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  COST_CENTER: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  COST_CENTER_CREATE_COST_CENTER: {
    READ: [Role.OWNER_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
  COST_CENTER_DASHBOARD: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  COST_CENTER_ACCOUNT_SEE_DETAILS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  COST_CENTER_ACCOUNT_EDIT_CARDS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },
  COST_CENTER_ACCOUNT_TOP_UP: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },
  COST_CENTER_EDIT_COST_CENTER_ADDRESS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
  },
  COST_CENTER_EDIT_COST_CENTER_DEFAULT: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
  COST_CENTER_ASSIGN_USERS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
  COST_CENTER_ASSIGN_INVOICING_RECIPIENTS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  SUBSCRIPTIONS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE],
    WRITE: [],
  },
  SUBSCRIPTIONS_MODIFY: {
    READ: [Role.OWNER_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },

  REPORTS_FILES: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  REPORTS_FILES_REPORTS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  REPORTS_FILES_TRANSACTION_HISTORY: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  REPORTS_FILES_INVOICING: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  REPORTS_FILES_GENERATE_REPORTS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  REPORTS_FILES_DOWNLOAD_INVOICE_AND_ATTACHMENTS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  HELP: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },
  HELP_SEND_A_TICKET: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  NOTIFICATIONS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [],
  },
  NOTIFICATIONS_MANAGE_NOTIFICATIONS: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  USER_ACCOUNT_EDIT: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
  },

  COMPANY_ACCOUNT_EDIT: {
    READ: [Role.OWNER_ROLE, Role.ADMINISTRATOR_ROLE, Role.OPERATOR_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
  COMPANY_ACCOUNT_DELEGATE_PERMISSIONS: {
    READ: [Role.OWNER_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
  COMPANY_ACCOUNT_LIST_OF_USERS: {
    READ: [Role.OWNER_ROLE],
    WRITE: [],
  },
  COMPANY_ACCOUNT_INVITE_USER: {
    READ: [Role.OWNER_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
  COMPANY_ACCOUNT_EDIT_USER: {
    READ: [Role.OWNER_ROLE],
    WRITE: [Role.OWNER_ROLE],
  },
};
